import axios from "axios";
import TokenService from "./token.service";
import { baseURL, ShubAuth, ShubURL } from "../core";
import constants from "../Util/constants";
import { useEffect } from "react";
import { postXdataSave } from '../Util/xdataSave'

const userToken = localStorage.getItem("jToken")

// Shub calls: updateXdata, addNewPartner

const token2 = ShubAuth;
const userRequest = axios.create({
    headers: {
        Authorization: `Bearer ${token2}`
    }
})

const updateXdata = async (ID, xdataPayload) => {
    //585a39dbe43900100017e9e8
    // console.log("saved xdata for ID>>>>", ID,":",xdataPayload);
    const ShubResponse = await postXdataSave(userRequest, constants.SHUB_URL + `/local/xdata/${ID}`, xdataPayload);
    return ShubResponse
};

// -------------------------------------------------
// Hostaway (Phase 1) - hub-side endpoints. Use a dedicated axios instance
// so we send X-API-KEY (the shub gate uses this header) alongside the
// existing Bearer token. constants.X_API_KEY is the shared shub API key.
// -------------------------------------------------
const hostawayRequest = axios.create({
    headers: {
        Authorization: `Bearer ${token2}`,
        'X-API-KEY': constants.X_API_KEY,
    },
})
const listHostawayPartners = async () => {
    return hostawayRequest.get(constants.SHUB_URL + `/hostaway-accounts`)
}
// The account's existing Extranet login rows (HW-<id>), so the connect form can refuse an
// email that would create a second login (Asana 1218719984465196).
const findPartnerLoginRows = async (hwAccountId) => {
    const res = await userRequest.get(constants.SHUB_URL + `/local/partners?accountId=${encodeURIComponent(hwAccountId)}&limit=20&skip=0`)
    // The hub answers a failed query with HTTP 200 + success:false; that is a failed lookup.
    if (res?.data?.success !== true) throw new Error(res?.data?.error || 'partner lookup failed')
    const rows = Array.isArray(res?.data?.partners) ? res.data.partners : []
    return rows.filter((r) => r && r.accountId === hwAccountId)
}
const connectHostawayPartner = async (payload) => {
    // payload: { accountId: number, clientSecret: string, vtAccountId?: string, email: string, pmName?: string }
    return hostawayRequest.post(constants.SHUB_URL + `/hostaway-connect`, payload)
}
const triggerHostawaySync = async (accountId) => {
    return hostawayRequest.get(constants.SHUB_URL + `/hostaway-syncing`, { params: { accountId } })
}
// Booking WRITES (hub booking-write contract 2026-10-06): the hub accepts only the ADMIN
// extranet session here, not X-API-KEY — so these use userRequest (Bearer placeholder that
// the hub-session transport swaps for the session), not hostawayRequest. Create also needs
// an Idempotency-Key (one per user submit, reused on retries of it), bookingIntent and
// expectedTotal + currency in the body.
const createHostawayBooking = async (payload, { idempotencyKey } = {}) => {
    // payload: { accountId, listingMapId, channelId, guestName, guestEmail,
    //            arrivalDate, departureDate, totalPrice, currency, numberOfGuests,
    //            bookingIntent, expectedTotal }
    if (!idempotencyKey) throw new Error('createHostawayBooking: idempotencyKey is required')
    return userRequest.post(constants.SHUB_URL + `/hostaway-create-reservation`, payload,
        { headers: { 'Idempotency-Key': String(idempotencyKey) } })
}
const cancelHostawayBooking = async (payload) => {
    // payload: { accountId, reservationId, cancelledBy?: 'host'|'guest' }
    return userRequest.put(constants.SHUB_URL + `/hostaway-cancel-reservation`, payload)
}

// -------------------------------------------------
// BookingPal Reservation Management (OUTBOUND to BookingPal via VTHub/SHub).
// All hit /local/bookingpal/* (auth.verifyAccessToken Bearer).
// -------------------------------------------------
const bpCheckAvailability = async (params) => {
    // params: { listing_id, start_date, nights }
    return userRequest.get(constants.SHUB_URL + `/local/bookingpal/check-availability`, { params })
}
const bpQuote = async (params) => {
    // params: { listing_id, start_date, nights, number_of_guests }
    return userRequest.get(constants.SHUB_URL + `/local/bookingpal/quote`, { params })
}
// Unified live quote — ONE endpoint, every PM (Guesty/SH/RU/DH/BP/HW/VIB/INV).
// Hub is this repo's fixed constants.SHUB_URL; auth = the static Bearer
// (accepted by verifyAccessToken) already on userRequest, plus x-api-key. No new
// token/env. Returns the normalized NET DTO: { ok, available, netTotal, currency,
// propertyCurrency, rent, taxesTotal, feesTotal, securityDeposit, lineItems, ... }.
const getUnifiedQuote = async ({ listingId, checkIn, checkOut, guests, currency }) => {
    return userRequest.get(constants.SHUB_URL + `/api/booking/quote`, {
        params: { listingId, checkIn, checkOut, guests, currency },
        // Bearer (on userRequest) already passes verifyAccessToken on both hubs;
        // x-api-key is an extra accepted credential where the repo ships one (VT).
        headers: constants.X_API_KEY ? { 'x-api-key': constants.X_API_KEY } : {},
    })
}
// Unified reservation-CREATE — the create counterpart to getUnifiedQuote. POSTs
// to <SHUB_URL>/api/booking/create; the hub dispatches by the listingId prefix,
// re-quotes NET server-side, and returns { ok, source, reservationId,
// confirmationCode }.
// Hub booking-write contract 2026-10-06: a WRITE route. The hub accepts only the
// ADMIN extranet session here — the Bearer on userRequest (ShubAuth is the
// placeholder the hub-session transport swaps for the session). x-api-key is no
// longer accepted on writes, so it is not sent. REQUIRED: idempotencyKey (one per
// user submit, the SAME key on every retry of that submit), bookingIntent, and
// expectedTotal + currency (the NET total/currency from the quote the page showed;
// the hub re-quotes and answers 409 price_changed if they drift).
const bookingCreate = async ({ listingId, checkIn, checkOut, guests, currency, guest, idempotencyKey,
    bookingIntent = 'approved_request', expectedTotal }) => {
    if (!idempotencyKey) throw new Error('bookingCreate: idempotencyKey is required')
    if (!(Number(expectedTotal) > 0) || !currency) throw new Error('bookingCreate: expectedTotal and currency from the quote are required')
    return userRequest.post(constants.SHUB_URL + `/api/booking/create`,
        { listingId, checkIn, checkOut, guests, currency, guest, bookingIntent, expectedTotal: Number(expectedTotal) },
        { headers: { 'Idempotency-Key': String(idempotencyKey) } })
}
// Unified reservation-CANCEL — the cancel counterpart to bookingCreate. POSTs
// { reservationId } (the channelReservationId returned at create, stored on the
// reservation-of-record as bpConfirmationId) to <SHUB_URL>/api/booking/cancel;
// the hub finds the reservation and dispatches the per-PM cancel (idempotent, a
// 2nd call → duplicate). Same auth as create (admin session; no x-api-key). Throws
// on non-2xx (incl. 404 for a reservation not born from the unified flow).
// legacy: true lets an ADMIN session cancel by raw RU ReservationID a booking the hub
// has no record of (made before it kept records). The hub ignores it for any other
// session, so callers send it only for admins.
const cancelBooking = async (reservationId, { legacy = false } = {}) => {
    const body = { reservationId }
    if (legacy === true) body.legacy = true
    return userRequest.post(constants.SHUB_URL + `/api/booking/cancel`, body)
}
// Partner reserve after a Flywire payment — POST <SHUB_URL>/api/booking/reserve. The
// hubs refuse partner sessions on /create; /reserve admits them and is payment-gated
// (a verified Flywire callback must match payment.ref). One booking per payment.ref, so
// no Idempotency-Key. Auth = the session the userRequest Bearer already carries. Body:
// build it with Util/partnerReserve.js buildReservePayload.
const bookingReserve = async (payload) => {
    return userRequest.post(constants.SHUB_URL + `/api/booking/reserve`, payload)
}
const bpQuotePreview = async (params) => {
    // params: { reservation_id, start_date, nights, number_of_guests }
    return userRequest.get(constants.SHUB_URL + `/local/bookingpal/quote-preview`, { params })
}
const bpCreateReservation = async (payload) => {
    // payload: { listing_id, start_date, nights, number_of_guests, total, currency, guest{...} }
    return userRequest.post(constants.SHUB_URL + `/local/bookingpal/reservation`, payload)
}
const bpReservationDetails = async (params) => {
    // params: { confirmation_id }
    return userRequest.get(constants.SHUB_URL + `/local/bookingpal/reservation-details`, { params })
}
const bpModifyReservation = async (payload) => {
    // payload: { confirmation_id, channel, start_date, nights, body }
    return userRequest.put(constants.SHUB_URL + `/local/bookingpal/reservation-modification`, payload)
}
const bpCancelReservation = async (params) => {
    // params: { confirmation_code, confirmation_id }
    return userRequest.delete(constants.SHUB_URL + `/local/bookingpal/reservation`, { params })
}
// Create the reservation-of-record for the BP reserve + Flywire flow. VT
// extranet writes to VT-BE (RESERVATION_API), which derives source='BP' from
// propertyId and verifies the Flywire payment via its own webhook.
const addReservation = async (payload) => {
    return axios.post(`${constants.RESERVATION_API}/reservation/add-reservation`, payload, {
        headers: { authorization: `Bearer ${userToken}` },
    })
}
// Scheduled-payment (Flywire Payment Request / 50-50 instalment) flow.
const createScheduledReservation = async (payload) =>
    axios.post(`${constants.RESERVATION_API}/reservation/scheduled/create`, payload, { headers: { authorization: `Bearer ${userToken}` } })
const recordScheduledReference = async (payload) =>
    axios.post(`${constants.RESERVATION_API}/reservation/scheduled/record-reference`, payload, { headers: { authorization: `Bearer ${userToken}` } })
const getScheduledStatus = async (reservationID) =>
    axios.get(`${constants.RESERVATION_API}/reservation/scheduled/status`, { params: { id: reservationID }, headers: { authorization: `Bearer ${userToken}` } })
// Currency exchange rates (for the BP reserve modal's USD conversion when the
// charge portal is USD-only, e.g. the Flywire demo portal). Same hub endpoint
// the Partners page uses; returns an array of { currency_code, conversion_rates }.
const getExchangeRates = async () => {
    return userRequest.get(constants.SHUB_URL + `/xchange`)
}
// Cancel/decline the reservation-of-record. Points at RESERVATION_API (VT-BE for
// VT), whose updateReservationStatus runs the Flywire refund/hold-release side
// effect and returns `flywireAction` — the refund behaves EXACTLY like VT-FE.
const bpDeclineReservation = async (reservationID, status = 'declined', extraFields = undefined) => {
    const body = { status, ...(extraFields && typeof extraFields === 'object' ? extraFields : {}) }
    const response = await axios.put(`${constants.RESERVATION_API}/reservation/update-reservation`, body, {
        params: { id: reservationID },
        headers: { 'Content-Type': 'application/json', authorization: `Bearer ${userToken}` },
    })
    // Normalize for UI callers (mirrors VT-FE updateReservationStatus).
    return { success: response?.status >= 200 && response?.status < 300, ...response.data }
}


const addNewPartner = async (payload) => {
    const ShubResponse = await userRequest.post(constants.SHUB_URL + `/services/guesty/channel/account`,
        payload);
    console.log("Shub response to add new partner:", ShubResponse)
    return ShubResponse
};

const updatePartner = async (accountId, payload, source) => {
    console.log("updateAPI:", accountId, payload, source)
    const ShubResponse = await userRequest.post(constants.SHUB_URL + `/update/` + accountId, payload, source);
    return ShubResponse
};

const updateEPartner = async (partnerId, payload) => {
    console.log("updateAPI:", partnerId, payload)
    //const ShubResponse = await userRequest.post(constants.SHUB_URL + `/update-external-partner/` + partnerId, payload);
    const ShubResponse = await userRequest.post(constants.SHUB_URL + `/eps/update-external-partner/` + partnerId, payload);
    return ShubResponse
};

const deletePartner = async (accountId, source) => {
    const ShubResponse = await userRequest.post(constants.SHUB_URL + `/delete/` + accountId, source);
    return ShubResponse
};

const deleteEPartner = async (partnerId) => {
    //const ShubResponse = await userRequest.post(constants.SHUB_URL + `/delete-external-partner/` + partnerId);
    const ShubResponse = await userRequest.post(constants.SHUB_URL + `/eps/delete-external-partner/` + partnerId);
    return ShubResponse
};

const uploadCSVPartner = async (partnerId,data) => {
    const ShubResponse = await userRequest.post(constants.SHUB_URL + `/eps/csv-external-partner/` + partnerId, data);
    return ShubResponse
};
const uploadSelectedListings = async (partnerId,data) => {
    const ShubResponse = await userRequest.post(constants.SHUB_URL + `/eps/upload-external-partner-listings/` + partnerId, data);
    return ShubResponse
};

const resyncPartner = async (accountId, source) => {
    const ShubResponse = await userRequest.post(constants.SHUB_URL + `/resync/` + accountId + '/' + source);
    return ShubResponse
};
const resyncBartPartner = async () => {
    const ShubResponse = await userRequest.post(constants.SHUB_URL + `/sync-villainstbarth`);
    return ShubResponse
};

const resyncBartPartnerWithProgress = (onProgress, onComplete, onError) => {
  const controller = new AbortController();
  
  fetch(`${constants.SHUB_URL}/sync-villainstbarth-progress`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token2}`,
      'x-api-key': constants.X_API_KEY,
      'Accept': 'text/event-stream',
      'Cache-Control': 'no-cache'
    },
    signal: controller.signal
  })
  .then(response => {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    
    function processStream() {
      return reader.read().then(({ done, value }) => {
        if (done) {
          console.log('Stream completed');
          return;
        }
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              onProgress(data);
              
              if (data.type === 'final_summary' || data.type === 'error') {
                if (data.type === 'final_summary') {
                  onComplete(data);
                } else {
                  onError(data);
                }
                return;
              }
            } catch (error) {
              console.error('Error parsing SSE data:', error);
              onError({ type: 'error', message: 'Failed to parse progress data' });
            }
          }
        }
        
        return processStream();
      });
    }
    
    return processStream();
  })
  .catch(error => {
    console.error('Fetch error:', error);
    if (error.name !== 'AbortError') {
      onError({ type: 'error', message: 'Connection to sync service lost' });
    }
  });

  return () => controller.abort();
};
const resyncBartPartnerWithProgressold = (onProgress, onComplete, onError) => {
  const eventSource = new EventSource(`${constants.SHUB_URL}/sync-villainstbarth-progress`);
  
  eventSource.onmessage = function(event) {
    try {
      const data = JSON.parse(event.data);
      onProgress(data);
      
      if (data.type === 'final_summary' || data.type === 'error') {
        eventSource.close();
        if (data.type === 'final_summary') {
          onComplete(data);
        } else {
          onError(data);
        }
      }
    } catch (error) {
      console.error('Error parsing SSE data:', error);
      onError({ type: 'error', message: 'Failed to parse progress data' });
    }
  };

  eventSource.onerror = function(event) {
    console.error('SSE connection error:', event);
    eventSource.close();
    onError({ type: 'error', message: 'Connection to sync service lost' });
  };

  // Return a function to manually close the connection
  return () => eventSource.close();
};
const activatePartner = async (accountId, source) => {
    const ShubResponse = await userRequest.put(constants.SHUB_URL + `/products/all` , {accountId , source});
    return ShubResponse
};

const updateTravelAgency = (data, data1) => {
    const headersForImage = {
        "authorization": `Bearer ${userToken}`,
    }

    return axios.post(`${baseURL}/travel-agency/update-travel-agencies?agency_id=${data1}`, data, {
        headers: headersForImage
    }).then((response) => {
        return response.data;
    });
}

const updateAgentApi = (agent_id, data) => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.post(`${baseURL}/agent/update-agent?agent_id=${agent_id}`, data, {
        headers: headersForupdate
    }).then((response) => {
        localStorage.setItem("agent", JSON.stringify(response.data.doc));
        return response.data;
    });

};

const updateProfileApi = (agent_id, agency_id, data, AgencyPayload) => {
    if (agent_id) { updateAgentApi(agent_id, data) }
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.post(`${baseURL}/travel-agency/update-travel-agencies?agency_id=${agency_id}`, AgencyPayload, {
        headers: headersForupdate
    }).then((response) => {
        localStorage.setItem("travelAgency", JSON.stringify(response?.data?.data?.agencyDetails[0]));
        return response.data;
    });

};



const AddNewClientApi = (data) => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.post(`${baseURL}/client/create-client`, data, {
        headers: headersForupdate
    })
        .then((response) => {
            return response.data;
        });
};

const UpdateClientApi = (client_id, data) => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.post(`${baseURL}/client/update-client?client_id=${client_id}`, data, {
        headers: headersForupdate
    })
        .then((response) => {
            return response.data;
        });
};

const VerifyCodeApi = (data) => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.post(`${baseURL}/agent/verify-code`, data, {
        headers: headersForupdate
    }).then((response) => {
        return response.data;
    });
};

const ChnagePasswordApi = (data) => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.post(`${baseURL}/agent/change-password`, data, {
        headers: headersForupdate
    }).then((response) => {
        return response.data;
    });
};

const GetAgency = (data) => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.get(`${baseURL}/travel-agency/get-travel-agencies?agency_id=${data}`, {
        headers: headersForupdate
    })
        .then((response) => {
            return response.data;
        });
};

const GetProfile = (data) => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.get(`${baseURL}/agent/get-profile?agent_id=${data}`, {
        headers: headersForupdate
    })
        .then((response) => {
            return response.data;
        });
};

const ForgotPasswordApi = (data) => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.post(`${baseURL}/agent/forget-password`, data, {
        headers: headersForupdate
    })
        .then((response) => {
            return response.data;
        });
};

const ForgotPasswordApiVTHUB = (data) => {
    // The hub-session placeholder, NOT the VT-Backend jToken: this runs on the logged-out
    // "Resend access details" page, where jToken is null. src/Util/hubSession.js swaps the
    // placeholder for a PUBLIC hub session, which VTHub's verifyAccessToken accepts on this
    // route (it used to be ungated on VTHub; gated like SHub's since 2026-10-14). Same as
    // extranet-sh's ForgotPasswordApiSHUB.
    const headersForupdate = {
        "authorization": constants.SHUB_TOKEN,
    }
    return axios.post(`${constants.SHUB_URL}/local/resend-extranet-access-details-to-partner-email`, data, {
        headers: headersForupdate
    })
        .then((response) => {
            return response.data;
        });
};

const GetReservation = (data) => {
    console.log(userToken, "userToken")
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.get(`${baseURL}/reservation/get-reservations`, {
        params: data,
        headers: headersForupdate,
    }).then((response) => {
        return response.data;
    });
};

const GetClientCstApi = (name, email) => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.get(`${baseURL}/client/get-clients?limit=20&search=${name}&email=${email}
    `, {
        headers: headersForupdate,
    }).then((response) => {
        return response.data;
    });
};

const GetSingleClientApi = (agentId, agencyId, clientId) => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.get(`${baseURL}/client/get-client?agent_id=${agentId}&agency_id=${agencyId}&client_id=${clientId}
    `, {
        headers: headersForupdate,
    }).then((response) => {
        return response.data;
    });
};

const GetWishListLog = () => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.get(`${baseURL}/wishlist/get-wishlists`, {
        headers: headersForupdate
    })
        .then((response) => {
            return response.data;
        });
};

const AgentSignup = (data) => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.post(`${baseURL}/agent/signup`, data, {
        headers: headersForupdate
    }).then((response) => {
        return response.data;
    });
};

// This call used to carry SMILING HOUSE's bearer against VillaTracker's own hub —
// a cross-brand copy-paste. Both are accepted by VTHub (verified live: each returns
// 200 on /local/destinations), so this is a like-for-like swap, but shipping one
// brand's credential inside the other's bundle means rotating SH's silently breaks
// VT. It now uses VT's own token, from the single place it is defined.
const DestinationsOptions = () => {
    console.log("loading Destinations from SHUB...")
    const headersForupdate = {
        "authorization": constants.SHUB_TOKEN,
    }
    return axios.get(`https://api.villatracker.com/local/destinations`, {
        headers: headersForupdate
    })
        .then((response) => {
            const regions = response?.data.data[0]?.allRegions
            const countries = response?.data.data[0]?.allCountries
            const cities = response?.data.data[0]?.allCities
            const destinations = response?.data.data[0]?.destinations
            console.log('loaded destinations:',destinations)
            localStorage.setItem('destinations', JSON.stringify(destinations))
            localStorage.setItem('regions', JSON.stringify(regions))
            localStorage.setItem('countries', JSON.stringify(countries))
            localStorage.setItem('cities', JSON.stringify(cities))
            return response.data;
        })

};

const getHotDesinationsApi = () => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.get(`${baseURL}/hotdestination/get-hotdestinations`, {
        headers: headersForupdate
    }).then((response) => {
        return response.data;
    });
};

const approveAgent = (data, agent_id) => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.post(`${baseURL}/agent/approve-agent?agent_id=${agent_id}`, data, {
        headers: headersForupdate
    }).then((response) => {
        return response.data;
    });
};


const travelAgencyApproval = (data, agency_id) => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.post(`${baseURL}/travel-agency/approve?agency_id=${agency_id}`, data, {
        headers: headersForupdate
    }).then((response) => {
        return response.data;
    });
};

const trianglLuxuryApiPerId = (id) => {
    const token2 = ShubAuth;
    const headersForupdate = {
        Authorization: `Bearer ${token2}`
    }
    console.log(`getting listings https://api.villatracker.com/local/listings/${id}`)
    return axios.get(`https://api.villatracker.com/local/listings/${id}`, {
        headers: headersForupdate
    }).then((response) => {
        console.log("Res API:", response.data)
        return response.data;
    });
};

const trianglLuxuryApi = (params) => {
    const token2 = ShubAuth;
    const headersForupdate = {
        Authorization: `Bearer ${token2}`
    }
    console.log(params)
    const queryString = Object.keys(params).map(key => key + '=' + params[key]).join('&');
    console.log(`getting listings https://api.villatracker.com/local/listings?${queryString}`)
    return axios.get(`https://api.villatracker.com/local/listings?${queryString}`, {
        //    return axios.get(constants.SHUB_URL_LOCAL + `/local/listings?${data}`, {
        headers: headersForupdate
    }).then((response) => {
        console.log("Res API:", response.data)
        return response.data;
    });
};

const sendEmailApi = (data) => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.post(`${baseURL}/email/send-email`, data, {
        headers: headersForupdate
    }).then((response) => {
        return response.data;
    });
};

const hotdestinationAddLikeApi = (id) => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.patch(`${baseURL}/hotdestination/add-one-like?id=${id}`, {}, {
        headers: headersForupdate
    }).then((response) => {
        return response.data;
    });
};

const hotdestinationRemoveLikeApi = (id) => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.delete(`${baseURL}/hotdestination/remove-one-like?id=${id}`, {
        headers: headersForupdate
    }).then((response) => {
        return response.data;
    });
};


const AddSubAgentApi = (data) => {
    const headersForupdate = {
        "authorization": `Bearer ${userToken}`,
    }
    return axios.post(`${baseURL}/agent/add-sub-agent`, data, {
        headers: headersForupdate
    }).then((response) => {
        return response.data;
    });
};

const updateReservationStatus = async (reservationData, status, statusUpdatedBy, reservationUniqueID) => {

  try {
    const response = await axios.put(
      `${ShubURL}/eps/update-reservations/${reservationData.partnerId}`,
      { reservationID:reservationData.reservationID, status, statusUpdatedBy, reservationUniqueID, reservationData }, 
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${reservationData.partnerToken}`,
        },
      }
    );
    console.log('Reservation status updated:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating reservation status:', error);
    throw error;
  }
};


const declineReservation = async(reservationData, status, statusUpdatedBy) => {
  try {
    const response = await axios.put(
      `${ShubURL}/eps/decline-reservations/${reservationData.partnerId}`,
      { reservationID:reservationData.reservationID, status, statusUpdatedBy, reservationData }, 
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${reservationData.partnerToken}`,
        },
      }
    );
    console.log('Reservation status updated:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating reservation status:', error);
    throw error;
  }
}


const AuthService = {
    updateXdata,
    addNewPartner,
    updatePartner,
    updateEPartner,
    deletePartner,
    deleteEPartner,
    resyncPartner,
    resyncBartPartner,
    resyncBartPartnerWithProgress,
    activatePartner,
    hotdestinationAddLikeApi,
    hotdestinationRemoveLikeApi,
    sendEmailApi,
    GetWishListLog,
    AgentSignup,
    GetReservation,
    addReservation,
    createScheduledReservation,
    recordScheduledReference,
    getScheduledStatus,
    getExchangeRates,
    updateProfileApi,
    AddNewClientApi,
    GetProfile,
    DestinationsOptions,
    updateTravelAgency,
    GetClientCstApi,
    GetSingleClientApi,
    getHotDesinationsApi,
    approveAgent,
    UpdateClientApi,
    VerifyCodeApi,
    ChnagePasswordApi,
    ForgotPasswordApi,
    travelAgencyApproval,
    trianglLuxuryApi,
    AddSubAgentApi,
    trianglLuxuryApiPerId,
    uploadSelectedListings,
    updateReservationStatus,
    declineReservation,
    ForgotPasswordApiVTHUB,
    listHostawayPartners,
    connectHostawayPartner,
    findPartnerLoginRows,
    triggerHostawaySync,
    createHostawayBooking,
    cancelHostawayBooking,
    bpCheckAvailability,
    bpQuote,
    getUnifiedQuote,
    bookingCreate,
    bookingReserve,
    cancelBooking,
    bpQuotePreview,
    bpCreateReservation,
    bpReservationDetails,
    bpModifyReservation,
    bpCancelReservation,
    bpDeclineReservation,
};

export default AuthService;
